import type { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import {
  Button,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import dayjs, { type Dayjs } from 'dayjs';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import QuietEmpty from '@/components/QuietEmpty';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import {
  fetchContactOptions,
  fetchCustomerOptions,
  mergeOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import {
  createReferral,
  deleteReferral,
  queryReferrals,
  updateReferral,
} from '@/services/bff/referral.api';
import type { ContactItem } from '@/services/bff/types/contact';
import type { CustomerItem } from '@/services/bff/types/customer';
import type {
  ReferralItem,
  ReferralReferrerType,
  ReferralTargetType,
} from '@/services/bff/types/referral';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import styles from './style.module.less';

const REFERRAL_PAGE_SIZE = 100;
const NEW_ROW_KEY = '__new_referral__';
const TABLE_SCROLL_X = 1200;

export type ReferralFormItem = {
  uuid?: string;
  referrerType?: ReferralReferrerType;
  referrerUuid?: string;
  referrerName?: string;
  referralTime?: Dayjs;
  isPrimary?: boolean | number;
  profitShareRatio?: number;
  remark?: string;
  status?: number;
};

type EditableReferralRow = ReferralFormItem & {
  rowKey: string;
  isNew?: boolean;
};

type NamePath = string | number | Array<string | number>;

const REFERRER_TYPE_OPTIONS = [
  { label: '线索', value: 'lead' },
  { label: '联系人', value: 'contact' },
  { label: '客户', value: 'customer' },
] satisfies Array<{ label: string; value: ReferralReferrerType }>;

const REFERRER_TYPE_LABEL_MAP: Record<ReferralReferrerType, string> = {
  lead: '线索',
  contact: '联系人',
  customer: '客户',
};

const normalizeText = (value: unknown) => String(value || '').trim();

const toPath = (name: NamePath) => (Array.isArray(name) ? name : [name]);

const buildRowKey = (item: ReferralFormItem, index: number) =>
  normalizeText(item.uuid) ||
  `${item.referrerType || 'unknown'}-${item.referrerUuid || 'unknown'}-${index}`;

const normalizeEditableRow = (
  row?: Partial<EditableReferralRow>,
): EditableReferralRow => ({
  rowKey: normalizeText(row?.rowKey) || NEW_ROW_KEY,
  uuid: normalizeText(row?.uuid) || undefined,
  referrerType: row?.referrerType,
  referrerUuid: normalizeText(row?.referrerUuid) || undefined,
  referrerName: normalizeText(row?.referrerName) || undefined,
  referralTime: row?.referralTime ? dayjs(row.referralTime) : undefined,
  isPrimary: Boolean(row?.isPrimary),
  profitShareRatio:
    row?.profitShareRatio === undefined || row?.profitShareRatio === null
      ? undefined
      : Number(row.profitShareRatio),
  remark: normalizeText(row?.remark) || undefined,
  status: row?.status ?? 1,
  isNew: Boolean(row?.isNew),
});

export const mergeEditingReferralDraft = (
  items: ReferralFormItem[],
  editingRowKey?: string,
  draftRow?: ReferralFormItem & { rowKey?: string; isNew?: boolean },
): ReferralFormItem[] => {
  if (!editingRowKey || !draftRow) {
    return items;
  }

  const normalizedDraft = normalizeEditableRow(draftRow);
  if (editingRowKey === NEW_ROW_KEY) {
    return [...items, normalizedDraft];
  }

  return items.map((item, index) =>
    buildRowKey(item, index) === editingRowKey ? normalizedDraft : item,
  );
};

export const toReferralFormValues = (
  items?: ReferralItem[],
): ReferralFormItem[] =>
  (items || []).map((item) => ({
    uuid: item.uuid,
    referrerType: item.referrerType,
    referrerUuid: item.referrerUuid,
    referrerName: item.referrerName,
    referralTime: item.referralTime ? dayjs(item.referralTime) : undefined,
    isPrimary: Boolean(item.isPrimary),
    profitShareRatio: item.profitShareRatio,
    remark: item.remark,
    status: item.status ?? 1,
  }));

const normalizeReferralFormItems = (items?: ReferralFormItem[]) => {
  const rows = items || [];
  const seenPairs = new Set<string>();
  return rows.reduce<
    Array<{
      uuid?: string;
      referrerType: ReferralReferrerType;
      referrerUuid: string;
      referralTime?: string;
      isPrimary?: number;
      profitShareRatio?: number;
      remark?: string;
      status?: number;
    }>
  >((acc, item, index) => {
    const referrerType = item?.referrerType;
    const referrerUuid = normalizeText(item?.referrerUuid);
    const remark = normalizeText(item?.remark) || undefined;
    const hasContent =
      Boolean(referrerType) ||
      Boolean(referrerUuid) ||
      Boolean(item?.referralTime) ||
      Boolean(item?.isPrimary) ||
      item?.profitShareRatio !== undefined ||
      Boolean(remark);

    if (!hasContent) {
      return acc;
    }
    if (!referrerType || !referrerUuid) {
      throw new Error(`请完整填写第 ${index + 1} 条推荐人信息`);
    }

    const pairKey = `${referrerType}:${referrerUuid}`;
    if (seenPairs.has(pairKey)) {
      throw new Error('同一个对象下不能重复添加相同推荐人');
    }
    seenPairs.add(pairKey);

    acc.push({
      uuid: normalizeText(item?.uuid) || undefined,
      referrerType,
      referrerUuid,
      referralTime: item?.referralTime
        ? dayjs(item.referralTime).toISOString()
        : undefined,
      isPrimary: item?.isPrimary ? 1 : 0,
      profitShareRatio:
        item?.profitShareRatio === undefined || item?.profitShareRatio === null
          ? undefined
          : Number(item.profitShareRatio),
      remark,
      status: item?.status ?? 1,
    });
    return acc;
  }, []);
};

export async function syncTargetReferrals({
  targetType,
  targetUuid,
  values,
  initialItems,
}: {
  targetType: ReferralTargetType;
  targetUuid: string;
  values?: ReferralFormItem[];
  initialItems?: ReferralItem[];
}) {
  const normalizedTargetUuid = normalizeText(targetUuid);
  if (!normalizedTargetUuid) {
    throw new Error('缺少目标对象标识，无法保存推荐人关系');
  }

  const normalizedItems = normalizeReferralFormItems(values);
  const previousItems = (initialItems || []).filter((item) =>
    normalizeText(item.uuid),
  );
  const keepUuids = new Set(
    normalizedItems.map((item) => normalizeText(item.uuid)).filter(Boolean),
  );

  await Promise.all(
    normalizedItems.map((item) => {
      const payload = {
        referrerType: item.referrerType,
        referrerUuid: item.referrerUuid,
        targetType,
        targetUuid: normalizedTargetUuid,
        referralTime: item.referralTime,
        isPrimary: item.isPrimary,
        profitShareRatio: item.profitShareRatio,
        remark: item.remark,
        status: item.status ?? 1,
      };
      return item.uuid
        ? updateReferral(item.uuid, payload)
        : createReferral(payload);
    }),
  );

  await Promise.all(
    previousItems
      .filter((item) => !keepUuids.has(normalizeText(item.uuid)))
      .map((item) => deleteReferral(item.uuid)),
  );
}

export async function queryAllTargetReferrals({
  targetType,
  targetUuid,
}: {
  targetType: ReferralTargetType;
  targetUuid: string;
}): Promise<ReferralItem[]> {
  const normalizedTargetUuid = normalizeText(targetUuid);
  if (!normalizedTargetUuid) {
    return [];
  }

  const firstPage = await queryReferrals({
    targetType,
    targetUuid: normalizedTargetUuid,
    page: 1,
    pageSize: REFERRAL_PAGE_SIZE,
  });
  const pageCount = Math.ceil(firstPage.total / REFERRAL_PAGE_SIZE);
  if (pageCount <= 1) {
    return firstPage.list;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      queryReferrals({
        targetType,
        targetUuid: normalizedTargetUuid,
        page: index + 2,
        pageSize: REFERRAL_PAGE_SIZE,
      }),
    ),
  );
  return [...firstPage.list, ...remainingPages.flatMap((page) => page.list)];
}

function addKnownOptions(
  options: RelationOption[],
  items: Array<ReferralItem | ReferralFormItem>,
  type: ReferralReferrerType,
) {
  return mergeOptions(
    options,
    items
      .filter((item) => item.referrerType === type)
      .map((item) => ({
        value: String(item.referrerUuid || '').trim(),
        label:
          normalizeText(
            'referrerName' in item ? item.referrerName : undefined,
          ) || String(item.referrerUuid || '').trim(),
      }))
      .filter((item) => item.value),
  );
}

const getSelectLabel = (
  options: RelationOption[],
  value?: string,
  fallback?: string,
) => {
  const safeValue = normalizeText(value);
  if (!safeValue) {
    return fallback || '';
  }
  const matched = options.find((item) => item.value === safeValue);
  return matched?.label || fallback || safeValue;
};

export function ReferralFormSection({
  form,
  name = 'referrals',
  title = '推荐人',
  initialItems,
}: {
  form: FormInstance;
  name?: NamePath;
  title?: string;
  initialItems?: ReferralItem[];
}) {
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [tableItems, setTableItems] = useState<ReferralFormItem[]>([]);
  const [editingRowKey, setEditingRowKey] = useState<string>();
  const [draftRow, setDraftRow] = useState<EditableReferralRow>();
  const [optionLoading, setOptionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const fieldPath = useMemo(() => toPath(name), [name]);

  const resolveItemsFromForm = useCallback(() => {
    const currentValue = form.getFieldValue(fieldPath);
    return Array.isArray(currentValue)
      ? (currentValue as ReferralFormItem[])
      : [];
  }, [fieldPath, form]);

  const referralItems = useMemo(
    () => (Array.isArray(tableItems) ? tableItems : []),
    [tableItems],
  );

  const syncFormItems = useCallback(
    (items: ReferralFormItem[]) => {
      setTableItems(items);
      form.setFieldValue(fieldPath, items);
    },
    [fieldPath, form],
  );

  useEffect(() => {
    const formItems = resolveItemsFromForm();
    if (formItems.length) {
      setTableItems(formItems);
      return;
    }
    if (initialItems?.length) {
      setTableItems(toReferralFormValues(initialItems));
      return;
    }
    setTableItems([]);
  }, [initialItems, resolveItemsFromForm]);

  useEffect(() => {
    form.setFieldValue(
      fieldPath,
      mergeEditingReferralDraft(referralItems, editingRowKey, draftRow),
    );
  }, [draftRow, editingRowKey, fieldPath, form, referralItems]);

  const loadReferrerOptions = useCallback(
    async (
      type: ReferralReferrerType,
      keyword?: string,
      ensureValue?: string,
      ensureLabel?: string,
    ) => {
      setOptionLoading(true);
      try {
        const next =
          type === 'contact'
            ? await fetchContactOptions(keyword)
            : await fetchCustomerOptions(keyword);
        const merged = mergeOptions(
          [],
          ensureValue
            ? mergeOptions(
                next,
                [{ value: ensureValue, label: ensureLabel || ensureValue }],
                ensureValue,
              )
            : next,
          ensureValue,
        );
        if (type === 'contact') {
          setContactOptions(merged);
        } else {
          setCustomerOptions(merged);
        }
        return merged;
      } finally {
        setOptionLoading(false);
      }
    },
    [contactOptions, customerOptions],
  );

  useEffect(() => {
    if (!(initialItems || []).length && !tableItems.length) {
      return;
    }
    setContactOptions((prev) =>
      addKnownOptions(
        addKnownOptions(prev, initialItems || [], 'contact'),
        tableItems,
        'contact',
      ),
    );
    setCustomerOptions((prev) =>
      addKnownOptions(
        addKnownOptions(prev, initialItems || [], 'customer'),
        tableItems,
        'customer',
      ),
    );
  }, [initialItems, tableItems]);

  const editingType = draftRow?.referrerType;
  const currentOptions =
    editingType === 'contact' ? contactOptions : customerOptions;

  const stopEditing = useCallback(() => {
    setEditingRowKey(undefined);
    setDraftRow(undefined);
  }, []);

  const handleDraftChange = useCallback(
    (
      patch:
        | Partial<EditableReferralRow>
        | ((current: EditableReferralRow) => Partial<EditableReferralRow>),
    ) => {
      setDraftRow((current) => {
        const base = normalizeEditableRow(current);
        const nextPatch = typeof patch === 'function' ? patch(base) : patch;
        return normalizeEditableRow({
          ...base,
          ...nextPatch,
          rowKey: base.rowKey,
          isNew: base.isNew,
        });
      });
    },
    [],
  );

  const beginCreateRow = useCallback(() => {
    if (editingRowKey) {
      message.warning('请先保存或取消当前编辑行');
      return;
    }
    setEditingRowKey(NEW_ROW_KEY);
    setDraftRow(
      normalizeEditableRow({
        rowKey: NEW_ROW_KEY,
        status: 1,
        isPrimary: false,
        isNew: true,
      }),
    );
  }, [editingRowKey]);

  const beginEditRow = useCallback(
    async (item: ReferralFormItem, index: number) => {
      const rowKey = buildRowKey(item, index);
      if (editingRowKey && editingRowKey !== rowKey) {
        message.warning('请先保存或取消当前编辑行');
        return;
      }
      const nextDraft = normalizeEditableRow({
        ...item,
        rowKey,
        isNew: false,
      });
      setEditingRowKey(rowKey);
      setDraftRow(nextDraft);
      if (nextDraft.referrerType) {
        await loadReferrerOptions(
          nextDraft.referrerType,
          undefined,
          nextDraft.referrerUuid,
          nextDraft.referrerName || nextDraft.referrerUuid,
        );
      }
    },
    [editingRowKey, loadReferrerOptions],
  );

  const handleDelete = useCallback(
    (index: number) => {
      if (editingRowKey === NEW_ROW_KEY && draftRow?.rowKey === NEW_ROW_KEY) {
        const hasAppendedDraft = index >= referralItems.length;
        if (hasAppendedDraft) {
          stopEditing();
          return;
        }
      }
      const nextItems = referralItems.filter((_, current) => current !== index);
      syncFormItems(nextItems);
      if (editingRowKey === NEW_ROW_KEY) {
        stopEditing();
      }
    },
    [
      draftRow?.rowKey,
      editingRowKey,
      referralItems,
      stopEditing,
      syncFormItems,
    ],
  );

  const validateDraftRow = useCallback(
    (row: EditableReferralRow) => {
      const referrerType = row.referrerType;
      const referrerUuid = normalizeText(row.referrerUuid);
      if (!referrerType) {
        message.error('请选择来源');
        return false;
      }
      if (!referrerUuid) {
        message.error('请选择推荐人');
        return false;
      }
      const duplicateIndex = referralItems.findIndex((item, index) => {
        const currentRowKey = buildRowKey(item, index);
        if (currentRowKey === row.rowKey) {
          return false;
        }
        return (
          item.referrerType === referrerType &&
          normalizeText(item.referrerUuid) === referrerUuid
        );
      });
      if (duplicateIndex >= 0) {
        message.error('同一个对象下不能重复添加相同推荐人');
        return false;
      }
      return true;
    },
    [referralItems],
  );

  const handleSaveRow = useCallback(async () => {
    if (!draftRow || !editingRowKey) {
      return;
    }
    if (!validateDraftRow(draftRow)) {
      return;
    }
    try {
      setSaving(true);
      const currentType = draftRow.referrerType as ReferralReferrerType;
      const currentValue = normalizeText(draftRow.referrerUuid);
      const options =
        currentType === 'contact' ? contactOptions : customerOptions;
      const nextItem: ReferralFormItem = {
        uuid: normalizeText(draftRow.uuid) || undefined,
        referrerType: currentType,
        referrerUuid: currentValue,
        referrerName: getSelectLabel(
          options,
          currentValue,
          draftRow.referrerName || currentValue,
        ),
        referralTime: draftRow.referralTime,
        isPrimary: Boolean(draftRow.isPrimary),
        profitShareRatio:
          draftRow.profitShareRatio === undefined ||
          draftRow.profitShareRatio === null
            ? undefined
            : Number(draftRow.profitShareRatio),
        remark: normalizeText(draftRow.remark) || undefined,
        status: draftRow.status ?? 1,
      };

      const nextItems = [...referralItems];
      const currentIndex = referralItems.findIndex(
        (item, index) => buildRowKey(item, index) === editingRowKey,
      );
      if (currentIndex >= 0) {
        nextItems[currentIndex] = nextItem;
      } else {
        nextItems.push(nextItem);
      }
      syncFormItems(nextItems);
      stopEditing();
    } finally {
      setSaving(false);
    }
  }, [
    contactOptions,
    customerOptions,
    draftRow,
    editingRowKey,
    referralItems,
    stopEditing,
    syncFormItems,
    validateDraftRow,
  ]);

  const handleCancelEdit = useCallback(() => {
    stopEditing();
  }, [stopEditing]);

  const selectCreatedReferrer = useCallback(
    (type: ReferralReferrerType, uuid?: string, name?: string) => {
      const normalizedUuid = normalizeText(uuid);
      if (!normalizedUuid) {
        message.warning('对象已创建，请在推荐人字段中搜索选择');
        void loadReferrerOptions(type);
        return;
      }

      const option = {
        value: normalizedUuid,
        label: normalizeText(name) || normalizedUuid,
      };
      if (type === 'contact') {
        setContactOptions((prev) =>
          mergeOptions(prev, [option], normalizedUuid),
        );
      } else {
        setCustomerOptions((prev) =>
          mergeOptions(prev, [option], normalizedUuid),
        );
      }
      handleDraftChange({
        referrerType: type,
        referrerUuid: normalizedUuid,
        referrerName: option.label,
      });
    },
    [handleDraftChange, loadReferrerOptions],
  );

  const handleContactCreated = useCallback(
    (record?: ContactItem) => {
      setContactCreateOpen(false);
      selectCreatedReferrer('contact', record?.uuid, record?.name);
    },
    [selectCreatedReferrer],
  );

  const handleCustomerCreated = useCallback(
    (record?: CustomerItem) => {
      setCustomerCreateOpen(false);
      selectCreatedReferrer('customer', record?.uuid, record?.name);
    },
    [selectCreatedReferrer],
  );

  const renderEditableType = useCallback(
    (record: EditableReferralRow) => (
      <Select
        value={record.referrerType}
        options={REFERRER_TYPE_OPTIONS}
        placeholder="请选择来源"
        className={styles.cellField}
        onChange={(value) => {
          const nextType = value as ReferralReferrerType;
          handleDraftChange({
            referrerType: nextType,
            referrerUuid: undefined,
            referrerName: undefined,
          });
          void loadReferrerOptions(nextType);
        }}
      />
    ),
    [handleDraftChange, loadReferrerOptions],
  );

  const renderEditableReferrer = useCallback(
    (record: EditableReferralRow) => {
      const createActionLabel =
        record.referrerType === 'contact' ? '新建联系人' : '新建客户';

      return (
        <div className={styles.inlineEditorCell}>
          <Select
            showSearch
            allowClear
            filterOption={false}
            disabled={!record.referrerType}
            options={currentOptions}
            loading={optionLoading}
            value={record.referrerUuid}
            placeholder={
              record.referrerType ? '搜索并选择推荐人' : '请先选择来源'
            }
            className={styles.cellField}
            popupRender={(menu) =>
              record.referrerType ? (
                <div className={styles.referrerPopup}>
                  {menu}
                  <Divider className={styles.referrerPopupDivider} />
                  <button
                    type="button"
                    className={styles.referrerPopupAction}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => {
                      if (record.referrerType === 'contact') {
                        setContactCreateOpen(true);
                      } else {
                        setCustomerCreateOpen(true);
                      }
                    }}
                  >
                    {createActionLabel}
                  </button>
                </div>
              ) : (
                menu
              )
            }
            onFocus={() => {
              if (!record.referrerType) {
                return;
              }
              void loadReferrerOptions(
                record.referrerType,
                undefined,
                record.referrerUuid,
                record.referrerName,
              );
            }}
            onSearch={(keyword) => {
              if (!record.referrerType) {
                return;
              }
              void loadReferrerOptions(
                record.referrerType,
                keyword,
                record.referrerUuid,
                record.referrerName,
              );
            }}
            onChange={(value, option) => {
              const matchedLabel =
                !Array.isArray(option) && option && 'label' in option
                  ? normalizeText(option.label)
                  : undefined;
              handleDraftChange({
                referrerUuid: normalizeText(value) || undefined,
                referrerName: matchedLabel || undefined,
              });
            }}
          />
        </div>
      );
    },
    [currentOptions, handleDraftChange, loadReferrerOptions, optionLoading],
  );

  const editableData = useMemo<EditableReferralRow[]>(() => {
    const rows = referralItems.map((item, index) =>
      normalizeEditableRow({
        ...item,
        rowKey: buildRowKey(item, index),
        isNew: false,
      }),
    );
    if (editingRowKey === NEW_ROW_KEY && draftRow) {
      return [...rows, draftRow];
    }
    return rows.map((item) =>
      draftRow && item.rowKey === editingRowKey ? draftRow : item,
    );
  }, [draftRow, editingRowKey, referralItems]);

  const columns = useMemo<ProColumns<EditableReferralRow>[]>(
    () => [
      {
        title: '来源',
        dataIndex: 'referrerType',
        width: 120,
        fixed: 'left',
        render: (_, record) =>
          record.rowKey === editingRowKey ? (
            renderEditableType(record)
          ) : record.referrerType ? (
            <Tag bordered={false} className={styles.typeTag}>
              {REFERRER_TYPE_LABEL_MAP[record.referrerType]}
            </Tag>
          ) : (
            '-'
          ),
      },
      {
        title: '推荐人',
        dataIndex: 'referrerName',
        width: 200,
        fixed: 'left',
        render: (_, record) =>
          record.rowKey === editingRowKey
            ? renderEditableReferrer(record)
            : normalizeText(record.referrerName) ||
              normalizeText(record.referrerUuid) ||
              '-',
      },
      {
        title: '推荐时间',
        dataIndex: 'referralTime',
        width: 160,
        render: (_, record) =>
          record.rowKey === editingRowKey ? (
            <DatePicker
              value={record.referralTime}
              className={styles.cellField}
              placeholder="请选择日期"
              onChange={(value) => {
                handleDraftChange({
                  referralTime: value || undefined,
                });
              }}
            />
          ) : record.referralTime ? (
            dayjs(record.referralTime).format('YYYY-MM-DD')
          ) : (
            '-'
          ),
      },
      {
        title: '主介绍人',
        dataIndex: 'isPrimary',
        width: 120,
        render: (_, record) =>
          record.rowKey === editingRowKey ? (
            <Switch
              checked={Boolean(record.isPrimary)}
              checkedChildren="是"
              unCheckedChildren="否"
              onChange={(checked) => {
                handleDraftChange({ isPrimary: checked });
              }}
            />
          ) : record.isPrimary ? (
            '是'
          ) : (
            '否'
          ),
      },
      {
        title: '分润比例',
        dataIndex: 'profitShareRatio',
        width: 130,
        render: (_, record) =>
          record.rowKey === editingRowKey ? (
            <InputNumber
              min={0}
              max={100}
              precision={2}
              addonAfter="%"
              value={record.profitShareRatio}
              className={styles.cellField}
              placeholder="请输入"
              onChange={(value) => {
                handleDraftChange({
                  profitShareRatio:
                    value === null || value === undefined
                      ? undefined
                      : Number(value),
                });
              }}
            />
          ) : record.profitShareRatio === undefined ||
            record.profitShareRatio === null ? (
            '-'
          ) : (
            `${record.profitShareRatio}%`
          ),
      },
      {
        title: '备注',
        dataIndex: 'remark',
        width: 220,
        render: (_, record) =>
          record.rowKey === editingRowKey ? (
            <Input.TextArea
              rows={1}
              maxLength={500}
              showCount={false}
              value={record.remark}
              className={styles.cellTextarea}
              placeholder="补充背景信息"
              onChange={(event) => {
                handleDraftChange({ remark: event.target.value });
              }}
            />
          ) : (
            normalizeText(record.remark) || '-'
          ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 120,
        fixed: 'right',
        valueType: 'option',
        onCell: () => ({
          style: {
            whiteSpace: 'nowrap',
          },
        }),
        render: (_, record, index) => {
          if (record.rowKey === editingRowKey) {
            return (
              <Space size={12} wrap={false}>
                <a
                  className={styles.actionLink}
                  onClick={() => {
                    void handleSaveRow();
                  }}
                >
                  {saving ? '保存中' : '保存'}
                </a>
                <a className={styles.actionLink} onClick={handleCancelEdit}>
                  取消
                </a>
                {!record.isNew ? (
                  <Popconfirm
                    title="确认删除这条推荐人吗"
                    onConfirm={() => handleDelete(index)}
                  >
                    <a className={styles.actionLink}>删除</a>
                  </Popconfirm>
                ) : null}
              </Space>
            );
          }
          return (
            <Space size={12} wrap={false}>
              <a
                className={styles.actionLink}
                onClick={() => {
                  void beginEditRow(record, index);
                }}
              >
                编辑
              </a>
              <Popconfirm
                title="确认删除这条推荐人吗"
                onConfirm={() => handleDelete(index)}
              >
                <a className={styles.actionLink}>删除</a>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [
      beginEditRow,
      editingRowKey,
      handleCancelEdit,
      handleDelete,
      handleDraftChange,
      handleSaveRow,
      renderEditableReferrer,
      renderEditableType,
      saving,
    ],
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarMain}>
          {title ? <div className={styles.toolbarTitle}>{title}</div> : null}
        </div>
      </div>

      <div className={styles.tableWrap}>
        <ProTable<EditableReferralRow>
          cardBordered={false}
          search={false}
          options={false}
          toolBarRender={false}
          scroll={{ x: TABLE_SCROLL_X }}
          rowKey="rowKey"
          columns={columns}
          dataSource={editableData}
          rowClassName={(record) =>
            record.rowKey === editingRowKey ? styles.editingRow : ''
          }
          pagination={false}
          size="small"
          locale={{
            emptyText: <QuietEmpty />,
          }}
        />
      </div>

      <Button
        type="link"
        className={styles.addRowButton}
        onClick={beginCreateRow}
      >
        <span className={styles.addRowButtonText}>新增一条推荐人</span>
      </Button>

      {contactCreateOpen ? (
        <ContactEditModal
          open={contactCreateOpen}
          onOpenChange={setContactCreateOpen}
          width="92vw"
          context={undefined}
          zIndex={1200}
          currentMemberUuid={getCurrentMemberUuid()}
          onSuccess={handleContactCreated}
        />
      ) : null}
      {customerCreateOpen ? (
        <CustomerEditModal
          open={customerCreateOpen}
          onOpenChange={setCustomerCreateOpen}
          width="92vw"
          context={undefined}
          zIndex={1200}
          currentMemberUuid={getCurrentMemberUuid()}
          onSuccess={handleCustomerCreated}
        />
      ) : null}
    </div>
  );
}
